import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  Chip,
  Divider,
  Tooltip,
  IconButton,

} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import configServ from "../../services/config";
import dayjs from "dayjs";
import CustomDateRangePicker from "../../Components/DatePicker/CustomDateRangePicker";
import pdfImage from "../../Components/Assets/images/pdf.jfif";
import excelImage from "../../Components/Assets/images/excel.png";
import { useSelector } from "react-redux";
import DownloadChallanReportPdf from "../../Components/Download/DownloadChallanReportPdf";
import DownloadChallanReportExcel from "../../Components/Download/DownloadChallanReportExcel";

const ChallanReport = () => {
  // States
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRange, setDateRange] = useState([null, null]);
    const { company_id, companyName } = useSelector((state) => state.GlobalVariables);
  // Fetch API
  const fetchChallanReport = async () => {
    setLoading(true);
    try {
      const result = await configServ.getCustomerChallanData({
        company_id: company_id,
        search: search,
        page: page + 1, // API is 1-indexed
        per_page: rowsPerPage,
        start_date: dateRange[0]
          ? dayjs(dateRange[0]).format("YYYY-MM-DD")
          : "",
        end_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      });

      if (result.success) {
        setReport(result.data);
        setTotalCount(result.total || 0);
      } else {
        setReport([]);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReport([]);
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch on page, rowsPerPage or search change
  useEffect(() => {
    fetchChallanReport();
  }, [page, rowsPerPage, search, dateRange]);

    const [companyLogo, setCompanyLogo] = useState("");
    const [openBackdrop, setOpenBackdrop] = useState(false);
  
    const assignCompanyLogo = async () => {
      try {
        const result = await configServ.company_logo({ company_id: company_id });
        if (result && result.image) {
          // Assuming the result.image is already a base64 string
          setCompanyLogo(`data:image/png;base64,${result.image}`);
        } else {
          setCompanyLogo(null); // Set as null if no logo
        }
      } catch (e) {
        console.log(e);
        setCompanyLogo(null); // Handle error by clearing logo
      }
    };
  
    useEffect(() => {
      assignCompanyLogo();
    }, []);
  
    const handleDownloadPdf = () => {
      DownloadChallanReportPdf(report, companyName, companyLogo,setOpenBackdrop);
    };
  
    const handleDownloadExcel = () => {
      DownloadChallanReportExcel(report, companyName, setOpenBackdrop);
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="primary">
          Challan Report
        </Typography>

        {/* Filter & Action Container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            size="small"
            placeholder=" Customer/Challan No..."
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "gray", mr: 1 }} />,
            }}
            sx={{ width: 250 }}
          />

          {/* Custom Date Range Picker */}
          <CustomDateRangePicker value={dateRange} onChange={setDateRange} />

          {/* Export Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownloadPdf}>
                <img
                  src={pdfImage}
                  alt="pdf"
                  style={{ width: 28, height: 28 }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download Excel">
              <IconButton onClick={handleDownloadExcel}>
                <img
                  src={excelImage}
                  alt="excel"
                  style={{ width: 28, height: 28 }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Content Section */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : report.length > 0 ? (
        <>
          {report.map((customer) => (
            <Accordion
              key={customer.id}
              sx={{ mb: 1, borderRadius: "8px !important", overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    pr: 2,
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {customer.fname}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {customer.customer_code} | {customer.mobile}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${customer.challans?.length || 0} Challans`}
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ backgroundColor: "#fafafa" }}>
                {customer.challans?.map((challan) => (
                  <Paper
                    key={challan.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 2, borderLeft: "4px solid #1976d2" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                      >
                        <ReceiptIcon
                          sx={{
                            fontSize: 16,
                            verticalAlign: "middle",
                            mr: 0.5,
                          }}
                        />
                        No: {challan.challan_no}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        Total: ₹{challan.total_amount}
                      </Typography>
                    </Box>
                    <Typography variant="caption" display="block" gutterBottom>
                      Date:{" "}
                      {new Date(challan.challan_date).toLocaleDateString()}
                    </Typography>

                    <TableContainer component={Box} sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: "#eee" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Item Name
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: "bold" }}
                            >
                              Qty
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: "bold" }}
                            >
                              Rate
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: "bold" }}
                            >
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {challan.items?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.itemname}</TableCell>
                              <TableCell align="right">{item.qty}</TableCell>
                              <TableCell align="right">{item.rate}</TableCell>
                              <TableCell align="right">{item.amount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      ) : (
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <Typography color="textSecondary">No data found</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ChallanReport;
